/**
 * Unified sheet / viewable navigation for offline demo bundles and live ACC.
 */
import { useMemo } from "preact/hooks";
import { isUniversalSheet, type SheetInfo } from "../data/sheets";
import { useAccModelStore } from "../stores/accModelStore";
import { useProjectStore } from "../stores/projectStore";
import { useViewerStore } from "../stores/viewerStore";

/** Sheet list for the active model (APS viewables or demo project sheets). */
export function useActiveSheets(): SheetInfo[] {
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const viewables = useAccModelStore(s => s.viewables);
  const projectSheets = useProjectStore(s => s.getCurrentProject().sheets);

  return useMemo(() => {
    if (viewerUrn && viewables.length > 0) {
      return viewables.map(v => ({
        id: v.id,
        name: v.name,
        documentPath: "",
        kind: v.kind,
      }));
    }
    return projectSheets;
  }, [viewerUrn, viewables, projectSheets]);
}

/** Id of the sheet/view the viewer is showing. */
export function useActiveSheetId(): string {
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const currentSheetId = useViewerStore(s => s.currentSheetId);
  return viewerUrn && currentViewableId ? currentViewableId : currentSheetId;
}

/** True when the user is on a 3D model view (every dbId is in context). */
export function useIsUniversalView(): boolean {
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const viewables = useAccModelStore(s => s.viewables);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const currentSheetId = useViewerStore(s => s.currentSheetId);

  if (viewerUrn && currentViewableId) {
    return viewables.find(v => v.id === currentViewableId)?.kind === "3d";
  }
  return isUniversalSheet(currentSheetId);
}

/** Switch the viewer to a sheet or APS viewable. */
export function navigateToSheet(sheetId: string): void {
  const viewerUrn = useAccModelStore.getState().viewerUrn;
  if (viewerUrn) {
    useAccModelStore.getState().setViewable(sheetId);
  } else {
    useViewerStore.getState().setCurrentSheet(sheetId);
  }
}

export function findSheet(
  sheets: SheetInfo[],
  id: string | undefined
): SheetInfo | undefined {
  if (!id) return undefined;
  return sheets.find(s => s.id === id);
}
